import { DashboardState, ViewPart, Visibility } from "@kuika/kuika-cl-model";

// Collapse is offered in EVERY view that renders a panel — the affordance lives inside the
// panel and the edge tab that re-opens it is gated on the collapsed flag alone, so there is
// nothing to opt a view into and no way to end up collapsible-but-unreopenable.

// Every state whose `ViewPart.LeftPanel` case can return Visible, minus reportDesigner: its
// page is a fixed paper size, so widening re-centres it and the canvas jumps under the pointer.
const LEFT_RESIZABLE_STATES: readonly DashboardState[] = [
  DashboardState.design,
  DashboardState.readOnly,
  DashboardState.dataDiagramScheme,
  DashboardState.workFlow,
  DashboardState.scheduledJobs,
  DashboardState.apiManagement,
  DashboardState.zendesk,
  DashboardState.documentParameters
];

export const supportsLeftPanelResize = (state: DashboardState): boolean => LEFT_RESIZABLE_STATES.includes(state);

export const supportsRightPanelResize = (state: DashboardState): boolean => state === DashboardState.design;

export const LEFT_MIN_WIDTH = 238;
export const RIGHT_MIN_WIDTH = 286;

export const LEFT_MAX_WIDTH = 500;
export const RIGHT_MAX_WIDTH = 500;

/** What both panels' ceilings protect, and the only reason they exist. */
export const MIN_CANVAS_WIDTH = 320;

export const CSS_VAR_LEFT = "--left-panel-width";
export const CSS_VAR_RIGHT = "--right-panel-width";

export const PANEL_MARGIN_BUFFER_PX = 46;
export const LEFT_COLLAPSED_BUFFER_PX = 39;
export const RIGHT_COLLAPSED_BUFFER_PX = 50;
export const BOTH_COLLAPSED_TOTAL_PX = 100;

export const getCssVarPx = (name: string, fallbackPx: number): number => {
  const value = (document?.documentElement?.style?.getPropertyValue(name) || "").trim();
  const parsed = parseInt(value, 10);
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  return fallbackPx;
};

export const setCssVarPx = (name: string, px: number): void => {
  if (!document?.documentElement?.style) return;
  document.documentElement.style.setProperty(name, `${px}px`);
};

/**
 * The right panel occupies three different amounts of room and they must not be conflated: its
 * own width while on screen, the expand tab's gutter while collapsed, and nothing at all when
 * it is not rendered — it is selection-driven in most tools and hidden without CreateComponent
 * access, so the collapse flag alone cannot answer this.
 */
export const getLeftPanelMaxWidth = (sd: any): number => {
  // NaN here would reach the panel's width, which the DOM ignores silently — no error, panel
  // frozen at its last size. Browsers always report a number; test harnesses do not.
  const reported: number = typeof window !== "undefined" ? window.innerWidth : 1366;
  const viewport: number = Number.isFinite(reported) ? reported : 1366;
  const isRightCollapsed: boolean = sd?.isRightPanelCollapsed?.[sd?.dashboardState] === true;
  const isRightVisible: boolean = sd?.getVisibility
    ? sd.getVisibility(ViewPart.RightPanel) === Visibility.Visible
    : !isRightCollapsed;

  let rightVisualWidth: number = 0;
  if (isRightVisible) rightVisualWidth = readRightPanelWidth();
  else if (isRightCollapsed) rightVisualWidth = RIGHT_COLLAPSED_BUFFER_PX;

  const maxBasedOnViewport: number = viewport - rightVisualWidth - PANEL_MARGIN_BUFFER_PX - MIN_CANVAS_WIDTH;
  return Math.min(LEFT_MAX_WIDTH, Math.max(LEFT_MIN_WIDTH, maxBasedOnViewport));
};

export const clampLeftWidth = (px: number, sd: any): number => {
  const max = getLeftPanelMaxWidth(sd);
  return Math.max(LEFT_MIN_WIDTH, Math.min(px, max));
};

export const getRightPanelMaxWidth = (sd: any): number => {
  const viewport = typeof window !== "undefined" ? window.innerWidth : 1366;
  const isLeftCollapsed = sd?.isLeftPanelCollapsed?.[sd?.dashboardState];
  const leftVisualWidth = isLeftCollapsed ? LEFT_COLLAPSED_BUFFER_PX : readLeftPanelWidth();
  const maxBasedOnViewport = viewport - leftVisualWidth - PANEL_MARGIN_BUFFER_PX - MIN_CANVAS_WIDTH;
  const dynamicMax = Math.min(RIGHT_MAX_WIDTH, Math.max(RIGHT_MIN_WIDTH, maxBasedOnViewport));
  return dynamicMax;
};

export const clampRightWidth = (px: number, sd?: any): number => {
  const max = sd ? getRightPanelMaxWidth(sd) : RIGHT_MAX_WIDTH;
  return Math.max(RIGHT_MIN_WIDTH, Math.min(px, max));
};

export const readLeftPanelWidth = (): number => getCssVarPx(CSS_VAR_LEFT, LEFT_MIN_WIDTH);
export const readRightPanelWidth = (): number => getCssVarPx(CSS_VAR_RIGHT, RIGHT_MIN_WIDTH);

const LEFT_WIDTH_KEY = "kuika_leftPanelWidth";

const leftWidthKey = (state: DashboardState): string => `${LEFT_WIDTH_KEY}:${state}`;

/**
 * NOT clamped, deliberately — callers clamp what they display. Squashing the stored value would
 * lose the user's choice the first time the right panel opened, with no way back when the room
 * returned.
 */
export const readStoredLeftWidth = (state: DashboardState): number | null => {
  // Never throws, and this path also WRITES — it runs from componentDidMount, so a throw here
  // would take the whole panel down rather than just its width.
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(leftWidthKey(state));
    // Adopt the pre-split shared key once, then drop it so it cannot outlive its replacement.
    if (!raw && state === DashboardState.design) {
      raw = window.localStorage.getItem(LEFT_WIDTH_KEY);
      window.localStorage.removeItem(LEFT_WIDTH_KEY);
    }
  } catch {
    return null;
  }
  if (!raw) return null;
  const parsed: number = parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

export const writeStoredLeftWidth = (state: DashboardState, px: number): void => {
  try {
    window.localStorage.setItem(leftWidthKey(state), px.toString());
  } catch {
    // Blocked or full: the width still applies, it just won't come back next time.
  }
};
